import React from 'react';
import { CountryTable } from 'models/Country';
import type { ColumnsType } from 'antd/es/table';
import { PERMISSIONS } from 'constants/permissions';
import { LoadingOutlined } from '@ant-design/icons';
import { HasAccess } from 'components/common/accessControl';
import { FilterIcon, PlusIcon, VerifiedIcon, ViewIcon } from 'components/common/icons';
import {
  AddSupplierModal,
  Button,
  Col,
  Form,
  FormInstance,
  Image,
  Input,
  PageTitle,
  Row,
  SupplierStatus,
  Table,
  Tooltip,
} from 'components';
import { useSuppliers } from './useSuppliers';
import styles from './styles.module.scss';
import { Filters } from './filters';

const { Search } = Input;

type IndustryType = {
  name: string;
  id: string;
};

export interface SupplierDataType {
  id: string;
  uuid: string | number;
  name: string;
  country: CountryTable;
  industry: IndustryType;
  language: any;
  formattedCreatedDate: string;
  reason: string;
  status: string;
  verified: boolean;
}

export const Suppliers: React.FC = () => {
  const [filtersForm] = Form.useForm<FormInstance>();
  const {
    addModalState,
    onAddModalStateChange,
    onCloseFilters,
    onOpenFilters,
    openFilters,
    handleTableChange,
    routeChange,
    isLoading,
    pagination,
    onSearch,
    data,
    total,
    applyFilters,
    resetFilters,
  } = useSuppliers();

  const columns: ColumnsType<SupplierDataType> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      render: (_, { uuid }) => {
        return uuid;
      },
    },
    {
      title: 'Supplier Name',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      width: 200, // Set max width
      ellipsis: true, // Enable ellipsis
      render: (value, data) => {
        return (
          <Row className={'gap-6'} wrap={false} align={'middle'}>
            {data.verified && <VerifiedIcon color={'#05796B'} size={16} />}
            <Tooltip title={value}>
              <div className={'text-ellipsis'}>{value}</div>
            </Tooltip>
          </Row>
        );
      },
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      render: (country) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {country?.fileLocation && (
              <Image
                src={country?.fileLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{country?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Main Language',
      dataIndex: 'language',
      key: 'language',
      minWidth: 130,
      render: (language) => {
        return (
          <Row wrap={false} align={'middle'} className={'gap-12'}>
            {language?.flagLocation && (
              <Image
                src={language?.flagLocation}
                width={18}
                height={18}
                preview={false}
                style={{ verticalAlign: 'unset', width: '18px' }}
                className={'radius-circle'}
              />
            )}
            <Col>{language?.name}</Col>
          </Row>
        );
      },
    },
    {
      title: 'Industry',
      dataIndex: 'industry',
      key: 'industry',
      render: (industry) => industry?.name,
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: true,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status, data) => <SupplierStatus status={status} tooltip={data.reason} />,
    },
    {
      title: 'Created Date',
      dataIndex: 'formattedCreatedDate',
      key: 'createDate',
      render: (value) => value?.day,
      minWidth: 130,
      sorter: true,
    },
    {
      title: 'Last Updated Date',
      dataIndex: 'formattedLastModifiedDate',
      key: 'lastModifiedDate',
      render: (value) => value?.day,
      minWidth: 160,
      sorter: true,
    },
    {
      fixed: 'right',
      width: '36px',
      key: 'actions',
      render: (value) => (
        <Button onClick={() => routeChange(value.id)} type={'text'} size={'small'} icon={<ViewIcon />} />
      ),
    },
  ];

  return (
    <Col span={24} className={styles.main}>
      <Row gutter={[0, 24]}>
        <Col span={24}>
          <Row align={'middle'} justify={'space-between'}>
            <PageTitle title={'Suppliers'} />
            <HasAccess role={PERMISSIONS.suppliers.supplier.write}>
              <Button type={'primary'} onClick={onAddModalStateChange} icon={<PlusIcon color={'#FFFFFF'} />}>
                Add New
              </Button>
            </HasAccess>
          </Row>
        </Col>
        <Col span={24} className={styles.main_container}>
          <Row>
            <Col span={24}>
              <Row justify={'end'} className={styles.actions_container}>
                <Col style={{ width: '300px' }}>
                  <Search enterButton={false} placeholder={'Search'} onChange={(e) => onSearch(e.target.value)} />
                </Col>
                <Button color="default" variant="filled" onClick={onOpenFilters} icon={<FilterIcon />} />
              </Row>
            </Col>
            <Col span={24} style={{ paddingTop: '20px' }}>
              <HasAccess role={PERMISSIONS.suppliers.supplier.read}>
                <Table
                  tableLayout={'auto'}
                  bordered
                  scroll={{
                    x: true,
                  }}
                  rowKey="id"
                  size={'small'}
                  dataSource={data}
                  columns={columns}
                  loading={{
                    indicator: <LoadingOutlined style={{ fontSize: 48 }} spin />,
                    spinning: isLoading,
                  }}
                  locale={
                    isLoading
                      ? { emptyText: null } // Hide "No Data" text while loading
                      : {} // Use default AntD "No Data" icon when empty
                  }
                  onChange={handleTableChange}
                  pagination={{
                    position: ['bottomCenter'],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    size: 'default',
                    current: pagination.page,
                    pageSize: pagination.size,
                    total: total,
                  }}
                />
              </HasAccess>
            </Col>
          </Row>
        </Col>
      </Row>
      <Filters
        form={filtersForm}
        drawerOpen={openFilters}
        closeDrawer={onCloseFilters}
        applyFilters={applyFilters}
        resetFilters={resetFilters}
      />
      <AddSupplierModal modalState={addModalState} onModalStateChange={onAddModalStateChange} />
    </Col>
  );
};
